import { Box, SxProps, useMediaQuery, useTheme } from '@mui/material';
import { CSSProperties, ReactNode, useEffect, useRef, useState, TouchEvent } from 'react';
import { isNumber } from 'chart.js/helpers';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PageDotIndicator } from './PageDotIndicator';

type ListCarouselProps = {
    items: ReactNode[];
    carouselWrapperStyle?: SxProps;
    listStyle?: Omit<SxProps, 'gap'>;
    listItemStyle?: SxProps;
    listWrapperStyle?: Omit<SxProps, 'gap'>;
    minimalDiff?: number;
    arrowOpacity?: CSSProperties['opacity'];
};

const styles = {
    wrapper: {
        desktop: {},
        mobile: {},
    },
    item: {
        margin: 0,
        padding: 0,
    },
    arrows: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: 24,
        minWidth: 24,
    },
} as const;

export function ListCarousel(Props: ListCarouselProps) {
    const {
        items,
        carouselWrapperStyle,
        listStyle,
        listItemStyle,
        listWrapperStyle,
        minimalDiff = 30,
        arrowOpacity = 0.3,
    } = Props;

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const [layoutChanged, setLayoutChanged] = useState<number>(0);

    const currentPage = useRef<number>(0);
    const [pageWidths, setPageWidths] = useState<number[] | undefined>(undefined);

    const touchStartX = useRef<number | undefined>(undefined);

    const parentRef = useRef<HTMLUListElement>(null);
    const childRefs = useRef<(HTMLLIElement | null)[]>([]);

    // Calculate the width of each page.
    useEffect(() => {
        const parentWidth = parentRef.current?.clientWidth ?? undefined;
        if (parentWidth === undefined) {
            return;
        }

        const pageWidthsTemp = calculatePageWidths(items, childRefs.current ?? [], parentWidth);
        setPageWidths(pageWidthsTemp);
    }, [layoutChanged, items]);

    // Observe layout changes.
    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            setLayoutChanged((prev) => prev + 1);
        });

        if (parentRef.current) {
            observer.observe(parentRef.current);
        }
        const ref = parentRef.current;

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, []);

    if (!items.length) {
        return null;
    }

    function handleTouchStart(event: TouchEvent<HTMLUListElement>) {
        event.stopPropagation();
        touchStartX.current = event?.touches?.[0]?.clientX ?? undefined;
    }

    function handleTouchEnd(event: TouchEvent<HTMLUListElement>) {
        event.stopPropagation();
        const touchEndX = event?.changedTouches?.[0]?.clientX ?? undefined;

        if (touchStartX?.current === undefined || touchEndX === undefined) {
            return;
        }

        const stepsToMove = calculateStepsToMove(touchStartX.current, touchEndX, minimalDiff);
        if (stepsToMove === 0) {
            return;
        }

        move(stepsToMove);

        touchStartX.current = undefined;

        setLayoutChanged((prev) => prev + 1);
    }

    function move(steps: number) {
        const newPage = currentPage.current + steps;

        if (newPage < 0 || newPage >= (pageWidths ?? []).length) {
            // newPage is out of bounds.
            return;
        }

        currentPage.current = newPage;
        const pixelsToMove = calculateCurrentPageLayoutPosition(newPage, pageWidths ?? []) * -1;
        parentRef.current?.style.setProperty('transform', `translate(${pixelsToMove}px, 0)`);
    }

    const isFirstPage = currentPage?.current === 0;
    const isLastPage = currentPage?.current === (pageWidths ?? []).length - 1;

    return (
        <div className="list-carousel">
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', ...carouselWrapperStyle }}>
                {isDesktop ? (
                    <Box
                        sx={{
                            ...styles.arrows,
                            ...(isFirstPage ? { opacity: arrowOpacity } : {}),
                        }}
                        onClick={() => {
                            move(-1);
                            setLayoutChanged((prev) => prev + 1);
                        }}
                    >
                        <ChevronLeftIcon />
                    </Box>
                ) : null}
                <Box
                    sx={{
                        overflowX: 'hidden',
                        ...listWrapperStyle,
                    }}
                >
                    <Box
                        ref={parentRef}
                        component="ul"
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            paddingInlineStart: 0,
                            listStyle: 'none',
                            fontSize: 0,
                            margin: 0,
                            padding: 0,
                            position: 'relative',
                            transition: 'transform 0.25s ease-in-out',
                            willChange: 'transform',
                            ...(styles.wrapper[isDesktop ? 'desktop' : 'mobile']),
                            ...listStyle,
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        {items.map((item, index) => (
                            <Box
                                key={index}
                                component="li"
                                ref={(element: HTMLLIElement | null) => {
                                    childRefs.current[index] = element;
                                }}
                                sx={{
                                    ...styles.item,
                                    ...listItemStyle,
                                }}
                            >
                                { item }
                            </Box>
                        ))}
                    </Box>
                </Box>
                {isDesktop ? (
                    <Box
                        sx={{
                            ...styles.arrows,
                            ...(isLastPage ? { opacity: arrowOpacity } : {}),
                        }}
                        onClick={() => {
                            move(1);
                            setLayoutChanged((prev) => prev + 1);
                        }}
                    >
                        <ChevronRightIcon />
                    </Box>
                ) : null}
            </Box>
            {(pageWidths?.length ?? 0) > 1 ? (
                <PageDotIndicator
                    count={pageWidths?.length ?? 0}
                    currentPage={currentPage.current}
                    onSelect={(index) => {
                        move(index - currentPage.current);
                        setLayoutChanged((prev) => prev + 1);
                    }}
                />
            ) : null}
        </div>
    );
}

function calculatePageWidths(
    items: Readonly<ReactNode[]>,
    currentChildRefs: Readonly<(HTMLLIElement | null)[]>,
    parentWidth: number,
): number[] {
    let pageIndex = 0;
    const pageWidths: number[] = [];

    currentChildRefs.forEach((element) => {
        const pageExists = pageIndex in pageWidths;
        if (!pageExists) {
            pageWidths[pageIndex] = 0;
        }

        const itemWidth = element?.clientWidth ?? 0;

        if (pageWidths[pageIndex] === 0) {
            // There should always be at least one item on each page.
            pageWidths[pageIndex] = itemWidth;

            return;
        }

        if (pageWidths[pageIndex] + itemWidth > parentWidth) {
            // eslint-disable-next-line no-plusplus
            ++pageIndex;
            pageWidths[pageIndex] = 0;
        }

        pageWidths[pageIndex] += itemWidth;
    });

    return pageWidths;
}

function calculateCurrentPageLayoutPosition(page: number, pageWidths: number[]): number {
    let pixels = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const pageIndex in pageWidths) {
        if (!isNumber(pageIndex)) {
            // eslint-disable-next-line no-continue
            continue;
        }

        if (Number(pageIndex) >= page) {
            break;
        }

        pixels += pageWidths[pageIndex];
    }

    return pixels;
}

function calculateStepsToMove(touchStartX: number, touchEndX: number, minimalDiff: number): number {
    const diff = touchEndX - touchStartX;

    if (Math.abs(diff) < minimalDiff) {
        // The swipe was too small.
        return 0;
    }

    return diff > 0 ? -1 : 1;
}
